import {
    FunctionComponent,
    useContext,
    createContext,
    useState,
    useEffect,
} from "react"
import { getFragmentData } from "../generated"
import { IDENTITY_FIELDS_FRAGMENT } from "../graphql/fragments/Identity"
import { IDENTITY_NORTH_AMERICAN_PHONE_NUMBER_FRAGMENT } from "../graphql/fragments/telephoneNumber/telephoneNumber"
import { useQuery } from "@apollo/client"
import { VIEWER } from "../graphql/queries/viewer"
import { ORGANIZATION_WITH_DESCENDANTS_FIELDS_FRAGMENT } from "../graphql/fragments/organizations"
import {
    OrganizationWithDescendantsFieldsFragment,
    SubjectRole,
} from "../generated/graphql"
import { IdentityFieldsFragment } from "../generated/graphql"
import { LabelValueOptionType } from "../types/commonTypes"
import { OrganizationMembership } from "../types/organizationMembership"

export interface ViewerStateInterface {
    organizationAndDescendants: LabelValueOptionType[]
    currentOrganization: LabelValueOptionType
    role: SubjectRole
    identity: IdentityFieldsFragment
    userId: string
    smsConsent: boolean
    emailConsent: boolean
    getPhoneNumber: (formatted?: boolean) => string | undefined
    organizationMembership: OrganizationMembership
}

interface ViewerContextInterface {
    getViewer: () => ViewerStateInterface
}

// Create context
const ViewerContext = createContext<undefined | ViewerContextInterface>(
    undefined
)

// Create provider
const ViewerProvider: FunctionComponent<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [viewerState, setViewerState] = useState<
        ViewerStateInterface | undefined
    >(undefined)
    const { data } = useQuery(VIEWER)

    useEffect(() => {
        // This effect hook is used to set state for the results of viewer data
        if (!data) return

        const organizationsAndDescendantsFields = getFragmentData(
            ORGANIZATION_WITH_DESCENDANTS_FIELDS_FRAGMENT,
            data.viewer.organization
        )

        const organizationAndDescendants = createUserOrganizationObject(
            organizationsAndDescendantsFields
        )

        const identityFields = getFragmentData(
            IDENTITY_FIELDS_FRAGMENT,
            data.viewer.identity
        )

        const phoneNumber = getFragmentData(
            IDENTITY_NORTH_AMERICAN_PHONE_NUMBER_FRAGMENT,
            identityFields?.telephoneNumber
        )

        if (!identityFields) {
            throw new Error(
                "Viewer Provider - Identity Fields returned as null or undefined from viewer query.  "
            )
        }

        const orgMembership = data.viewer.organizationMemberships[0]

        const initialState: ViewerStateInterface = {
            organizationAndDescendants: organizationAndDescendants,
            currentOrganization: {
                label: organizationsAndDescendantsFields.name,
                value: organizationsAndDescendantsFields.id,
            },
            identity: identityFields,
            userId: data.viewer.id,
            role: data.viewer.role,
            smsConsent: identityFields.smsConsent,
            emailConsent: identityFields.emailConsent,
            getPhoneNumber: (formatted = false) => {
                if (phoneNumber) {
                    if (formatted) {
                        return phoneNumber.formatted
                    }

                    return phoneNumber.concise
                }
            },
            organizationMembership: {
                id: orgMembership.id,
                role: orgMembership.role,
                organizationId: orgMembership.organization.id,
                managedAccess: orgMembership.managedAccess,
            },
        }

        setViewerState(initialState)
    }, [data])

    const getViewer = () => {
        if (viewerState === undefined) {
            throw new Error(
                "Viewer Provider - Viewer state  was undefined. ViewerState should have been set be the result of viewer query before children were rendered."
            )
        }
        return viewerState
    }

    // Context Values
    const contextValues = {
        getViewer,
    }

    return (
        <ViewerContext.Provider value={contextValues}>
            {viewerState && children}
            {!viewerState && <></>}
        </ViewerContext.Provider>
    )
}

// use context hook
const useViewerContext = () => {
    const context = useContext(ViewerContext)

    if (context === undefined) {
        throw new Error(
            " UseViewerContext error.  Viewer Context must be used within a child  Viewer Provider"
        )
    }

    return context
}

export { ViewerProvider, useViewerContext }

const createUserOrganizationObject = (
    organization: OrganizationWithDescendantsFieldsFragment
): LabelValueOptionType[] => {
    const organizations: LabelValueOptionType[] = []

    const rootOrg = organization
    const rootOrgFields: LabelValueOptionType = {
        value: rootOrg.id,
        label: rootOrg.name,
    }

    const descendants = rootOrg.descendants
    organizations.push(rootOrgFields)

    const transformedDescendants = descendants.map((descendant) => {
        if (!descendant) {
            throw new Error(
                "CreateOrganizationsArray error. Descendant should not be null value."
            )
        }
        const { id, name } = descendant
        return { value: id, label: name }
    })

    organizations.push(...transformedDescendants)

    return organizations
}
